import {
  IPortalContactResponse,
  OrganisationFeatureFlags,
  QuoteStatus,
} from "shared";
import {
  NavLink,
  Outlet,
  useMatch,
  useResolvedPath,
} from "react-router-dom";

import { classNames } from "../../../common/lib/classNames";
import { PaymentMethodsCard } from "../PaymentMethods/PaymentMethodsCard";
import { card } from "../portalUi";

interface ContactPageProps {
  contactDetails: IPortalContactResponse;
}

/** Saved payment methods card, in the statement rail or under the other tabs */
export function SavedPaymentMethods({ contact, className }: { contact: IPortalContactResponse; className?: string }) {
  const enabled = contact.stripeEnabled
    && contact.stripeConnectAccount
    && contact.organisationFeatureFlags?.includes(OrganisationFeatureFlags.PORTAL_PAYMENT_METHODS);

  if (!enabled) {
    return null;
  }

  return (
    <div className={ classNames(card, `[&>div]:border-t-0`, className) }>
      <PaymentMethodsCard
        compact
        resourceType={ `contact` }
        code={ contact.hash }
        stripeConnectAccount={ contact.stripeConnectAccount }
        senderDetail={ contact.senderDetail }
      />
    </div>
  );
}

export function ContactPage({ contactDetails }: ContactPageProps) {
  // The outstanding statement shows payment methods in its rail, only when there is a statement to show
  const outstandingTab = !!useMatch({ path: useResolvedPath(``).pathname, end: true });
  const inRail = outstandingTab && contactDetails.statement.currencySections.length > 0;
  const quotesTitle = contactDetails?.language?.quotes || `Quotes & Estimates`;

  const tabs = [
    {
      name: `Outstanding Statement`,
      href: ``,
      count: contactDetails.statement?.totalOutstandingInvoiceCount,
    },
  ];

  if (contactDetails.showQuotes) {
    tabs.push({
      name: quotesTitle,
      href: `quotes`,
      count: contactDetails.quotes?.filter(q => q.status === QuoteStatus.SENT)?.length,
    });
  }

  if (contactDetails.allowViewPaid) {
    tabs.push({
      name: `Paid Invoices`,
      href: `paid`,
      count: contactDetails.statement?.totalPaidInvoiceCount,
    });
  }

  // Dont display the external account number if its identical to the name
  const description = contactDetails.statement.contact.externalAccountNumber === contactDetails.statement.contact.name
    ? undefined
    : contactDetails.statement.contact.externalAccountNumber;

  return (
    <div className={ `mx-auto flex max-w-[93.75rem] flex-col gap-5 px-[clamp(1rem,3vw,2rem)] lg:pt-2` }>
      <div className={ `flex flex-col gap-1.5` }>
        <h1 className={ `text-2xl font-semibold leading-tight tracking-[-0.01em] text-gray-900` }>
          { contactDetails.statement.contact.name }
        </h1>
        { description && <span className={ `text-sm text-gray-500` }>{ description }</span> }
        <span className={ `text-sm text-gray-500` }>
          { `Your customer portal for ${contactDetails.senderDetail?.customerFacingCompanyName}` }
        </span>
      </div>

      <nav className={ `flex gap-6 overflow-x-auto text-sm shadow-[inset_0_-1px_0_var(--color-gray-200)]` }>
        { tabs.map(tab => (
          <NavLink
            key={ tab.href }
            to={ tab.href }
            end
            className={ ({ isActive }) => classNames(
              `flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 pb-2.5 hover:no-underline`,
              isActive
                ? `border-paidniceGreen-600 font-semibold text-paidniceGreen-600 hover:text-paidniceGreen-600`
                : `border-transparent text-gray-500 hover:text-gray-900`,
            ) }
          >
            { ({ isActive }) => (
              <>
                { tab.name }
                { tab.count !== undefined && (
                  <span className={ classNames(
                    `rounded-full px-1.5 text-[0.6875rem]`,
                    isActive ? `bg-paidniceGreen-100 font-semibold text-paidniceGreen-800` : `bg-gray-100 font-medium text-gray-600`,
                  ) }>
                    { tab.count }
                  </span>
                ) }
              </>
            ) }
          </NavLink>
        )) }
      </nav>

      <Outlet />

      { !inRail && (
        <SavedPaymentMethods
          contact={ contactDetails }
          className={ `sm:max-w-md` }
        />
      ) }
    </div>
  );
}
